import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, FileText, ShieldCheck, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AdminPanel from "@/components/admin/AdminPanel";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { request } from "@/lib/api";

const STATES = [
  { value: "pending_qc", label: "Awaiting review" },
  { value: "published", label: "Published" },
  { value: "rejected", label: "On hold" },
];

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN");
}

export default function ReportQcQueue({ token, open = true, onClose, inline = false }) {
  const [state, setState] = useState("pending_qc");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reviewing, setReviewing] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await request(`/admin/reports?qc_status=${state}`, {}, token));
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [token, state]);

  useEffect(() => {
    if (!open) return;
    load();
    setError("");
    setReviewing(null);
  }, [open, load]);

  async function decide(report, decision) {
    setBusy(true);
    setError("");
    try {
      await request(
        `/admin/reports/${report.report_id}/review`,
        { method: "POST", body: JSON.stringify({ decision, note: note || null }) },
        token,
      );
      setNotice(
        decision === "published"
          ? `Published to ${report.booking?.beneficiary_name || "the employee"} — they've been notified.`
          : `Held. ${report.booking?.beneficiary_name || "The employee"} has not been told.`,
      );
      setReviewing(null);
      setNote("");
      await load();
    } catch (decideError) {
      setError(decideError.message);
    } finally {
      setBusy(false);
    }
  }

  const counts = data?.counts || {};

  return (
    <AdminPanel inline={inline} open={open} onClose={onClose} title="Reports — check before the employee sees them" testId="admin-report-qc">

        {error && <p className="form-error" role="alert" data-testid="qc-error">{error}</p>}
        {notice && <p className="profile-saved" role="status" data-testid="qc-notice">{notice}</p>}

        {!reviewing && (
          <>
            <Tabs value={state} onValueChange={setState}>
              <TabsList>
                {STATES.map((item) => (
                  <TabsTrigger key={item.value} value={item.value} data-testid={`qc-tab-${item.value}`}>
                    {item.label} ({counts[item.value] ?? 0})
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {state === "pending_qc" && (
              <p className="employee-muted">
                Nothing here has reached the employee. Check the result belongs to the person named on the booking, and
                that every expected file is present, before publishing.
              </p>
            )}

            <div className="package-table-wrap">
              <Table data-testid="qc-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Report for</TableHead>
                    <TableHead>Booking</TableHead>
                    <TableHead>Files</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Reviewed</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.reports || []).map((report) => (
                    <TableRow key={report.report_id} data-testid={`qc-row-${report.report_id}`}>
                      <TableCell>
                        <span className="package-name">{report.booking?.beneficiary_name || "Unmatched report"}</span>
                        <small className="employee-muted">
                          {report.booking
                            ? `${report.booking.relationship} · ${report.booking.employee_email}`
                            : `No booking matches order ${report.apollo_sub_order_id}`}
                        </small>
                      </TableCell>
                      <TableCell>
                        {report.booking ? (
                          <>
                            <div>{report.booking.package_name}</div>
                            <small className="employee-muted">
                              {report.booking.appointment_date} · {report.booking.medical_center_name}
                            </small>
                          </>
                        ) : (
                          <span className="employee-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell>{report.report_urls?.length || 0}</TableCell>
                      <TableCell className="employee-muted">{formatWhen(report.received_at)}</TableCell>
                      <TableCell>
                        {report.qc_status === "pending_qc" ? (
                          <Badge variant="outline">Not yet</Badge>
                        ) : (
                          <>
                            <Badge variant={report.qc_status === "published" ? "default" : "secondary"}>
                              {report.qc_status === "published" ? "Published" : "On hold"}
                            </Badge>
                            <small className="employee-muted"> {report.qc_by}</small>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => { setReviewing(report); setNote(""); }} data-testid={`qc-open-${report.report_id}`}>
                          {report.qc_status === "pending_qc" ? "Review" : "Open"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!data?.reports?.length && (
                    <TableRow>
                      <TableCell colSpan={6} className="employee-muted">
                        {state === "pending_qc" ? "Nothing waiting — every report has been reviewed." : "Nothing here."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {reviewing && (
          <div className="package-edit" data-testid="qc-review-panel">
            <div className="upload-summary">
              <strong>{reviewing.booking?.beneficiary_name || "Unmatched report"}</strong>
              <span>{reviewing.booking?.package_name}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setReviewing(null)} data-testid="qc-back"><X size={14} />Back</Button>
            </div>

            {!reviewing.booking && (
              <p className="package-loss-warning" data-testid="qc-unmatched-warning">
                <AlertTriangle size={15} />
                No booking matches order {reviewing.apollo_sub_order_id}. Do not publish without establishing whose result this is.
              </p>
            )}

            {reviewing.booking && (
              <div className="package-price-compare">
                <div><span className="profile-field__label">Employee</span><strong>{reviewing.booking.employee_email}</strong></div>
                <div><span className="profile-field__label">Appointment</span><strong>{reviewing.booking.appointment_date}</strong></div>
                <div><span className="profile-field__label">Centre</span><strong>{reviewing.booking.medical_center_name || "—"}</strong></div>
              </div>
            )}

            <span className="profile-field__label"><FileText size={13} />Files from the provider ({reviewing.report_urls?.length || 0})</span>
            <div className="qc-file-list">
              {(reviewing.report_urls || []).map((url, index) => (
                <a key={url} href={url} target="_blank" rel="noreferrer" data-testid={`qc-file-${index}`}>
                  <ExternalLink size={14} />File {index + 1}
                </a>
              ))}
              {!reviewing.report_urls?.length && <span className="employee-muted">The provider sent no files with this update.</span>}
            </div>

            {reviewing.qc_status !== "pending_qc" && (
              <p className="employee-muted" data-testid="qc-previous-decision">
                Already {reviewing.qc_status === "published" ? "published" : "held"} by {reviewing.qc_by} on {formatWhen(reviewing.qc_at)}
                {reviewing.qc_note ? ` — ${reviewing.qc_note}` : ""}
              </p>
            )}

            <label htmlFor="qc-note">Note (required when holding)</label>
            <Input id="qc-note" value={note} onChange={(event) => setNote(event.target.value)} placeholder="e.g. only 1 of 3 files present — chased Apollo" data-testid="qc-note-input" />

            <div className="upload-actions">
              <Button type="button" variant="outline" disabled={busy || reviewing.qc_status === "published"} onClick={() => decide(reviewing, "rejected")} data-testid="qc-hold">
                <AlertTriangle size={15} />Hold
              </Button>
              <Button type="button" disabled={busy || reviewing.qc_status === "published"} onClick={() => decide(reviewing, "published")} data-testid="qc-publish">
                <CheckCircle2 size={15} />{busy ? "Publishing…" : "QC done — publish"}
              </Button>
            </div>
            <p className="employee-muted">
              <ShieldCheck size={13} /> Publishing makes the report visible in the employee's portal and sends their
              email. Holding leaves both untouched.
            </p>
          </div>
        )}
    </AdminPanel>
  );
}
